import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, lastValueFrom } from 'rxjs';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { GET } from 'src/app/network/network.service';
import { KbRouter } from '../interfaces/kb-router';
import { KbProcedure } from '../interfaces/kb-procedure';
import { KbPlaybook } from '../interfaces/kb-playbook';
import { KbToolResult } from '../interfaces/kb-tool-result';

/**
 * BK-1381 — live knowledge base retrieval.
 *
 * Replaces the bundled `assets/blueride_admin_kb.md`, which pinned ~11.3k
 * tokens into every turn and went four months stale between releases.
 *
 * The KB team publishes to https://blueride.co/api/kb/*. Those endpoints send
 * no CORS headers and 404 on preflight, so the console reads them through the
 * BlueRide backend proxy under the existing gemini route group.
 *
 * Two pieces:
 *   - the *router* (~5.3k tokens) — operating contract plus a procedure index,
 *     pinned into systemInstruction at session start.
 *   - *procedures* / *playbooks* (~433 tokens each) — fetched on demand via
 *     Gemini function calls, so knowledge only costs tokens when it is used.
 */
@Injectable({ providedIn: 'root' })
export class KbStoreService {

  /** Router is re-fetched when older than this. Matches the WhatsApp agent's kb-store TTL. */
  private readonly TTL_MS = 5 * 60 * 1000;

  private routerText: string | null = null;
  private routerFetchedAt = 0;
  private inflight$: Observable<string> | null = null;

  /**
   * Procedure and playbook lookups, keyed `procedure:split-grade:en`.
   *
   * The model re-issues an identical call more often than you would expect: an
   * interruption restarts its turn, and context-window compression can evict a
   * body it already received, at which point it fetches the same procedure
   * again. Caching removes the redundant round-trip and its latency mid-
   * conversation. It does not reduce tokens — the model is still handed the
   * body each time it asks — so it is not a substitute for giving procedures
   * room to survive compression.
   *
   * Same TTL as the router, so a knowledge fix published mid-session is picked
   * up on the next call rather than being pinned for the whole conversation.
   */
  private toolCache = new Map<string, { result: KbToolResult; fetchedAt: number }>();
  private toolInflight = new Map<string, Promise<KbToolResult>>();

  @GET('v2/admin/schools/:schoolId/gemini/kb/router')
  private getRouterRequest(queryParams?: any) {
    return undefined as any;
  }

  @GET('v2/admin/schools/:schoolId/gemini/kb/procedure/:id')
  private getProcedureRequest(queryParams?: any, filterParams?: any, pathParams?: any) {
    return undefined as any;
  }

  @GET('v2/admin/schools/:schoolId/gemini/kb/playbook/:id')
  private getPlaybookRequest(queryParams?: any, filterParams?: any, pathParams?: any) {
    return undefined as any;
  }

  /**
   * Router markdown for the voice channel.
   *
   * `channel=voice` is load-bearing — it selects the speaking-not-writing
   * operating contract. Omitting it serves a default alias with the wrong
   * contract for this surface.
   *
   * On failure we serve the last good copy and carry on. There is deliberately
   * no bundled fallback: a silently stale KB is the exact problem this
   * replaces. With no last-good copy the error propagates and the session
   * refuses to start rather than answering from the model's own guesses.
   *
   * Fetched lazily, only when a session starts and the cached copy is older
   * than the TTL. There is no background refresh: a timer would keep pulling
   * 19.5KB every 5 minutes for the rest of the page's life, long after the call
   * ended. The cold fetch it would have saved runs in parallel with the access
   * token fetch anyway, so it costs nothing on the critical path.
   *
   * Note: Gemini Live fixes `systemInstruction` at setup, so the router cannot
   * be swapped inside a running session. "Refreshes within 5 minutes" means the
   * next session sees the change — which is what the acceptance criterion tests.
   */
  getRouter(): Observable<string> {
    const fresh = this.routerText !== null && (Date.now() - this.routerFetchedAt) < this.TTL_MS;
    if (fresh) return of(this.routerText!);
    if (this.inflight$) return this.inflight$;

    this.inflight$ = this.fetchRouter().pipe(
      catchError((err) => {
        if (this.routerText !== null) {
          console.warn('[KbStore] router refresh failed, serving last good copy:', err?.status ?? err);
          return of(this.routerText);
        }
        console.error('[KbStore] router unavailable and no cached copy:', err?.status ?? err);
        return throwError(() => err);
      }),
      tap(() => { this.inflight$ = null; }),
      shareReplay(1)
    );
    return this.inflight$;
  }

  private fetchRouter(): Observable<string> {
    return this.getRouterRequest({ channel: 'voice' }).pipe(
      map((res: KbRouter) => {
        const text = res?.content;
        if (!text || !text.trim()) throw new Error('empty router body');
        this.routerText = text;
        this.routerFetchedAt = Date.now();
        return text;
      })
    );
  }

  /** ids come from the router's Procedure index — e.g. `split-grade`. */
  getProcedure(id: string, lang: string): Promise<KbToolResult> {
    const wanted = lang === 'ar' ? 'ar' : 'en';
    return this.cached(`procedure:${id}:${wanted}`, async () => {
      try {
        const p: KbProcedure = await lastValueFrom(
          this.getProcedureRequest({ lang: wanted }, {}, { id })
        );
        return { status: 'ok', title: p.title, entry_path: p.entry_path, body: p.body } as KbToolResult;
      } catch (err) {
        return this.toFailure(err, `procedure "${id}"`);
      }
    });
  }

  getPlaybook(id: string): Promise<KbToolResult> {
    return this.cached(`playbook:${id}`, async () => {
      try {
        const p: KbPlaybook = await lastValueFrom(
          this.getPlaybookRequest({}, {}, { id })
        );
        return { status: 'ok', title: p.title, stages: p.stages } as KbToolResult;
      } catch (err) {
        return this.toFailure(err, `playbook "${id}"`);
      }
    });
  }

  /**
   * Cache-and-dedupe wrapper.
   *
   * `unavailable` results are deliberately not cached: a transient outage must
   * not pin "I can't look that up" for the rest of the TTL. `not_found` is
   * cached — the id genuinely does not exist, and the model tends to retry it.
   */
  private cached(key: string, fetcher: () => Promise<KbToolResult>): Promise<KbToolResult> {
    const hit = this.toolCache.get(key);
    if (hit && (Date.now() - hit.fetchedAt) < this.TTL_MS) return Promise.resolve(hit.result);

    const pending = this.toolInflight.get(key);
    if (pending) return pending;

    const run = fetcher().then(result => {
      if (result.status !== 'unavailable') {
        this.toolCache.set(key, { result, fetchedAt: Date.now() });
      }
      this.toolInflight.delete(key);
      return result;
    }).catch(err => {
      this.toolInflight.delete(key);
      throw err;
    });

    this.toolInflight.set(key, run);
    return run;
  }

  private toFailure(err: unknown, what: string): KbToolResult {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    if (status === 404) {
      return {
        status: 'not_found',
        message: `There is no ${what} in the knowledge base. Tell the admin you do not have that one and offer to pass it to the support team. Do not invent or guess the steps.`
      };
    }
    console.warn(`[KbStore] ${what} fetch failed (${status})`);
    return {
      status: 'unavailable',
      message: `The knowledge base could not be reached for ${what}. Tell the admin you cannot look that up right now and offer to pass it to the support team. Do not invent or guess the steps.`
    };
  }
}
